import {
    EnvironmentType,
    HostApplicationInformation,
    ISearchRoute as privateISearchRoute,
    ISiteScopeData,
    MidgardCDNOptions,
    SearchDispatcher,
    SearchInstrumentation,
    SearchInstrumenter,
    SearchLogger,
    SearchPageInterface,
    SearchPrefetchInterface,
    SearchStrings,
    ServiceEnvironment
} from '@1js/search-page-bootstrapper';
import { Injectable, isDevMode } from '@angular/core';
import { SearchConfigService } from '../../search-results/services/search-config.service';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { UserContextService } from '../../shared/shared-context/user-context.service';
import getSerpVersion from './search-page-version-selector';

/**
 * Re-exporting types from Bootstrapper that are used by this host-app
 * for clarity and single point of importing.
 */
export {
    ICacheItemStats,
    IDispatcher,
    IPersonClickTargetWrapperProps,
    ISearchRoute,
    ITokenProviderStats,
    IUsageEvent,
    IWrappedXMLHttpRequest,
    LogEvent,
    SearchConfiguration,
    SearchEnvironmentUrls,
    SearchInstrumentation,
    SearchInstrumenter,
    ServiceEnvironment,
    StartQueryOptions,
    Microsoft365Ring
} from '@1js/search-page-bootstrapper';

// Build ScopeRouterApi interface
interface ScopeRouterApi {
    createScopeRouter: CreateScopeRouter;
    isSiteScopeVertical: IsSiteScopeVertical;
    listenToRoute: ListenToRoute;
}

export type ListenToRoute = (listener: (route: privateISearchRoute) => void, immediate?: boolean) => () => void;

export type IsSiteScopeVertical = (verticalName: string | null | undefined) => boolean;

export type CreateScopeRouter = (
    instrumenter: SearchInstrumenter,
    scopeRouterExtension?: ScopeRouterExtension
    ) => ScopeRouter;

export type InstrumenterScopeData =
    | ISiteScopeData
    | { readonly personScope: unknown } // TODO: Update with scope data types from 1JS
    | { readonly topicScope: unknown }
    | { readonly meetingScope: unknown }
    | { readonly messageScope: unknown };

export type InstrumenterScopeType = 'site' | 'person' | 'topic' | 'meeting' | 'message' | 'global'; // TODO: Replace with IScopeType from 1JS

export type ScopeRouterExtensionOutput =
    | {
          scopeId?: InstrumenterScopeType;
          scopeData?: InstrumenterScopeData;
      }
    | undefined;

    export type ScopeRouterExtension = (
        route: privateISearchRoute,
        hostAppInfo: HostApplicationInformation
        ) => ScopeRouterExtensionOutput;

export type ScopeRouter = (route: privateISearchRoute) => void;

export { SearchDispatcher };

interface SearchPageInterfaceWithScopeRouterApi extends SearchPageInterface {
    getScopeRouterApi(): Promise<ScopeRouterApi>;
}

interface SearchPageWithScopeRouterApi extends SearchPageInterface {
    getScopeRouterApi(): Promise<ScopeRouterApi>;
}

/**
 * Combination of non bootstrapper and bootstrapper interfaces
 * for backwards compatibility
 */
export type Prefetch = SearchPrefetchInterface;
export type Main = SearchPageWithScopeRouterApi | SearchPageInterfaceWithScopeRouterApi;
export type Logger = SearchLogger;

// TODO: export it from @1js/search-page-bootstrapper
// tslint:disable-next-line:no-any
export type ILoggerSettings = Record<string, any>;
// tslint:disable-next-line:no-any
export type IMainSettings = Record<string, any>;

export interface SearchPageModuleCombined {
    loadMain: () => Promise<Main>;
    loadPrefetch: () => Promise<Prefetch>;
    loadStrings: () => Promise<SearchStrings>;
    loadSearchDispatcher: () => Promise<SearchDispatcher>;
    loadSearchInstrumentation: () => Promise<SearchInstrumentation>;
}

@Injectable({
    providedIn: 'root'
})
export class SearchPageBootstrapperService {
    // Used to identify OfficeHome from other FAST integrations in logs.
    public static clientId = 'officehome';
    public static uwpClientId = 'officehomeuwp';
    public static consumerClientId = 'officehomemsa';

    // The main Search Page Module
    public readonly searchPageModule: Promise<SearchPageModuleCombined> = new Promise(resolve => (this.resolveSearchPageModule = resolve));
    private resolveSearchPageModule: (searchPageModule: SearchPageModuleCombined) => void;

    private readonly microsoftSearchBootstrapperUrlOverridesEnabled: boolean;
    private readonly microsoftSearchBootstrapperKillSwitchesActive: string;

    // Some additional settings
    public hostAppId: string;
    public cdnOptions: MidgardCDNOptions;

    constructor(
        private readonly searchConfigService: SearchConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly userContextService: UserContextService
    ) {
        this.microsoftSearchBootstrapperUrlOverridesEnabled = this.searchConfigService.config.microsoftSearchBootstrapperUrlOverridesEnabled;
        this.microsoftSearchBootstrapperKillSwitchesActive = this.searchConfigService.config.microsoftSearchBootstrapperKillSwitchesActive;

        /**
         * Picking the correct hostAppId, some other places in code access the
         * IDs directly, and some access it through this service
         */
        this.hostAppId = this.getHostAppId();

        /**
         * Set CDN values
         */
        this.cdnOptions = this.getCdnOptions();

        /**
         * Load SERP modules
         */
        this.loadModules();
    }

    /**
     * Setting the correct hostAppId and CDN options
     */
    public getLokiEnvironment() {
        return (isDevMode() && 'Df') || this.resolveLokiEnvironment();
    }

    public resolveLokiEnvironment(): EnvironmentType {
        const environmentType = this.getSearchEnvironment();
        switch (environmentType) {
            case 'test':
                return 'Df';
            case 'prodbubble':
                return 'Msit';
            case 'gcc':
                return 'GccModerate';
            case 'pathfinder':
                return 'DoD';
            case 'trailblazer':
                return 'GccHigh';
            case 'gallatin':
                return 'Gallatin';
            case 'prod':
                return 'Prod';
            default:
                return 'Prod';
        }
    }

    public getSearchEnvironment(): ServiceEnvironment {
        const environmentType = this.searchConfigService.config.complianceEnvironment;

        if (this.userContextService.user.isGcc && environmentType === 'prod') {
            return 'gcc';
        }

        return environmentType;
    }

    private getHostAppId() {
        if (this.clientStartupConfigService.config.authType === AuthType.MSA) {
            return SearchPageBootstrapperService.consumerClientId;
        } else {
            if (this.capabilitiesService.capabilities.isPwaInUwp && this.capabilitiesService.capabilities.nativeSearchInHeaderEnabled) {
                return SearchPageBootstrapperService.uwpClientId;
            } else {
                return SearchPageBootstrapperService.clientId;
            }
        }
    }

    private getCdnOptions(): MidgardCDNOptions {
        return {
            environment: this.getLokiEnvironment()
        };
    }

    /**
     * Loading of the Search modules in one of two ways, depending on a flight:
     * 1. Through search-page-bootstrapper
     * 2. Legacy way of importing directly from dist packages
     */
    private async loadModules() {
        const searchPageModule = await this.getBootstrappedSearchModules();
        this.preloadBootstrapperModules(searchPageModule);
        this.resolveSearchPageModule(searchPageModule);
    }

    /**
     * Preload the search bootstrapper modules and keep ready
     */
    private preloadBootstrapperModules(searchPageModule: SearchPageModuleCombined): void {
        searchPageModule.loadPrefetch();
        searchPageModule.loadSearchDispatcher();
        searchPageModule.loadSearchInstrumentation();
        searchPageModule.loadStrings();
    }

    /**
     * Search Page Bootstrapper fetching of the searchPageModule
     */
    private async getBootstrappedSearchModules(): Promise<SearchPageModuleCombined> {
        const bootstrapperModule = await import(/* webpackChunkName: 'search-page-bootstrapper' */ '@1js/search-page-bootstrapper');

        const searchPageModule = await bootstrapperModule.loadSearchPage({
            loadingPattern: 'deepLink',
            cdnOptions: this.cdnOptions,
            hostAppId: this.hostAppId,
            //@ts-ignore
            preferredVersion: getSerpVersion(
                this.microsoftSearchBootstrapperKillSwitchesActive
            ),
            isUrlOverridingEnabled: this.microsoftSearchBootstrapperUrlOverridesEnabled
        });

        return Promise.resolve(searchPageModule);
    }
}
